import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FiChevronLeft, FiChevronRight, FiShoppingCart, FiCheck, FiMinus, FiPlus,
  FiTruck, FiShield, FiMessageSquare,
} from 'react-icons/fi';
import API from '../utils/api';
import { useCart } from '../context/CartContext';
import { resolvePrice as resolvePriceUtil, clampQty as clampQtyUtil } from '../utils/pricing';
import ProductCard from '../components/common/ProductCard';
import './ProductDetailPage.css';
import './catalog/CatalogPages.css';

const FALLBACK = 'https://images.unsplash.com/photo-1586339949916-3e9457bef6d3?w=600';

// Use shared pricing utilities
const resolvePrice = resolvePriceUtil;
const clampQty     = clampQtyUtil;

// QuoteModal removed — Get Quote now navigates to /get-quote page

/* ── Main component ── */
export default function ProductDetailPage() {
  const { seg1, seg2, seg3 } = useParams();
  const navigate   = useNavigate();
  const { addToCart } = useCart();

  const [product,   setProduct]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [notFound,  setNotFound]  = useState(false);
  const [related,   setRelated]   = useState([]);

  // Gallery
  const [activeImg, setActiveImg] = useState(0);

  // Options / variant
  const [selectedOptions, setSelectedOptions] = useState({}); // { optionGroupId: optionValue }
  const [activeVariant,   setActiveVariant]   = useState(null);

  // Quantity
  const [quantity, setQuantity] = useState(1);

  // UI
  const [addedToCart, setAddedToCart] = useState(false);
  const [activeTab,   setActiveTab]   = useState('description');
  const [showSticky,  setShowSticky]  = useState(false);
  const ctaRef = useRef(null);

  // ── Load product ────────────────────────────────────────────────
  const slug = seg3 || seg2 || seg1; // handle 2-level URL too

  useEffect(() => {
    setLoading(true);
    API.get(`/catalog/public/products/${slug}`)
      .then(r => {
        const p = r.data.product;
        setProduct(p);

        // Auto-select first active variant's options
        const firstV = (p.variants || []).find(v => v.status === 'active');
        if (firstV?.selectedOptions) {
          const init = {};
          firstV.selectedOptions.forEach(o => { init[o.optionGroupId] = o.optionValue; });
          setSelectedOptions(init);
          setActiveVariant(firstV);
          setQuantity(firstV.minimumQuantity || 1);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  // ── Load related ────────────────────────────────────────────────
  useEffect(() => {
    if (!product) return;
    const params = new URLSearchParams({ limit: 4 });
    if (product.subcategoryId) params.set('subcategoryId', product.subcategoryId);
    if (product.categoryId)    params.set('categoryId',    product.categoryId);
    API.get(`/catalog/public/products/${product.id}/related?${params}`)
      .then(r => setRelated(r.data.products || []))
      .catch(() => {});
  }, [product]);

  // ── Sticky CTA on scroll ────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => {
      if (!ctaRef.current) return;
      const rect = ctaRef.current.getBoundingClientRect();
      setShowSticky(rect.bottom < 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ── Variant resolution when options change ──────────────────────
  useEffect(() => {
    if (!product?.variants) return;
    const activeVariants = product.variants.filter(v => v.status === 'active');
    const selectedKeys   = Object.keys(selectedOptions);
    if (selectedKeys.length === 0) return;

    const match = activeVariants.find(v =>
      (v.selectedOptions || []).every(o => selectedOptions[o.optionGroupId] === o.optionValue)
    );

    if (match) {
      setActiveVariant(match);
      setQuantity(q => {
        const min = match.minimumQuantity || 1;
        const inc = match.quantityIncrement || 1;
        return clampQty(Math.max(q, min), min, inc);
      });
    } else {
      setActiveVariant(null);
    }
  }, [selectedOptions, product]);

  // ── Helpers ─────────────────────────────────────────────────────
  const allImages = useCallback(() => {
    if (!product) return [];
    const varImgs = activeVariant?.galleryImages?.length ? activeVariant.galleryImages
                  : activeVariant?.mainImage ? [activeVariant.mainImage] : [];
    const prodImgs = product.galleryImages?.length ? product.galleryImages
                   : product.mainImage ? [product.mainImage] : [];
    const imgs = varImgs.length ? varImgs : prodImgs;
    return imgs.length ? imgs : [FALLBACK];
  }, [product, activeVariant]);

  // Reset gallery when variant/product changes
  useEffect(() => { setActiveImg(0); }, [activeVariant]);

  const unitPrice = activeVariant ? resolvePrice(activeVariant.pricing, quantity) : null;
const totalPrice = unitPrice != null ? unitPrice * quantity : null;

  // ── Option groups to display ────────────────────────────────────
  const optionGroupsToShow = useCallback(() => {
    if (!product?.productOptions || !product?.variants) return [];
    const activeVars = product.variants.filter(v => v.status === 'active');
    const usedGroupIds = new Set(
      product.productOptions
        .filter(o => o.usedInVariants)
        .map(o => o.optionGroupId)
    );
    // Gather unique values per group from active variants
    const groups = {};
    for (const v of activeVars) {
      for (const o of (v.selectedOptions || [])) {
        if (!usedGroupIds.has(o.optionGroupId)) continue;
        if (!groups[o.optionGroupId]) groups[o.optionGroupId] = { id: o.optionGroupId, name: o.optionGroupName, values: [] };
        if (!groups[o.optionGroupId].values.includes(o.optionValue)) {
          groups[o.optionGroupId].values.push(o.optionValue);
        }
      }
    }
    return Object.values(groups);
  }, [product]);

  const handleOptionSelect = (groupId, value) => {
    setSelectedOptions(prev => ({ ...prev, [groupId]: value }));
  };

  const handleQtyChange = (delta) => {
    const min = activeVariant?.minimumQuantity || 1;
    const inc = activeVariant?.quantityIncrement || 1;
    setQuantity(prev => {
      const next = prev + delta * inc;
      return Math.max(min, next);
    });
  };

  const handleQtyInput = (val) => {
    const num = parseInt(val, 10);
    if (isNaN(num)) return;
    const min = activeVariant?.minimumQuantity || 1;
    const inc = activeVariant?.quantityIncrement || 1;
    setQuantity(clampQty(num, min, inc));
  };

  const handleGetQuote = () => {
    // Build a prefilled item that mirrors cart item shape
    const prefilled = {
      id:              product.id,
      name:            product.name,
      sku:             product.sku || '',
      slug:            product.slug,
      image:           activeVariant?.mainImage || product.mainImage || '',
      isCatalog:       true,
      variantId:       activeVariant?.id || null,
      variantName:     activeVariant?.name || '',
      variantSku:      activeVariant?.sku || '',
      selectedOptions: activeVariant?.selectedOptions || [],
      quantity,
      unitPrice:       unitPrice || 0,
      pricingTiers:    activeVariant?.pricing || [],
      minimumQuantity: activeVariant?.minimumQuantity || 1,
      quantityIncrement: activeVariant?.quantityIncrement || 1,
    };
    navigate('/get-quote', { state: { fromCart: false, prefilled } });
  };

  const handleAddToCart = () => {
    if (!product || !activeVariant) return;
    addToCart(product, {
      isCatalog:         true,
      variantId:         activeVariant.id,
      variantName:       activeVariant.name,
      variantSku:        activeVariant.sku,
      selectedOptions:   activeVariant.selectedOptions || [],
      quantity,
      unitPrice:         unitPrice || 0,
      pricingTiers:      activeVariant.pricing || [],
      minimumQuantity:   activeVariant.minimumQuantity,
      quantityIncrement: activeVariant.quantityIncrement,
      variantImage:      activeVariant.mainImage || product.mainImage || '',
    });
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2500);
  };

  // ── JSON-LD ─────────────────────────────────────────────────────
  const jsonLd = product ? {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: product.name,
    description: product.shortDescription || '',
    sku: product.sku,
    image: allImages(),
    brand: { '@type': 'Brand', name: 'Angel Printers' },
    ...(product.startingPrice != null && {
      offers: {
        '@type': 'Offer',
        priceCurrency: 'INR',
        price: parseFloat(product.startingPrice),
        availability: 'https://schema.org/InStock',
        url: window.location.href,
      },
    }),
  } : null;

  // ── States ──────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ paddingTop: 120, textAlign: 'center' }}>
      <div className="spinner" />
    </div>
  );

  if (notFound || !product) return (
    <div style={{ padding: '120px 0', textAlign: 'center' }}>
      <h2>Product not found</h2>
      <Link to="/products" className="btn btn-primary" style={{ marginTop: 16 }}>Back to Products</Link>
    </div>
  );

  const images     = allImages();
  const groups     = optionGroupsToShow();
  const catSlug = product.category?.slug || '';
const subSlug = product.subcategory?.slug || '';
  const specs      = product.specifications || [];
  const hasVariants = product.variants?.length > 0;

  return (
    <>
      <Helmet>
        <title>{product.seoTitle || `${product.name} | Angel Printers`}</title>
        <meta name="description" content={product.seoDescription || product.shortDescription || ''} />
        <link rel="canonical" href={window.location.href} />
        {jsonLd && (
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        )}
      </Helmet>

      <div className="catalog-detail-page">
        {/* Breadcrumb header */}
        <div className="catalog-detail-header">
          <div className="container">
            <nav className="breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link><FiChevronRight size={13} />
              {product.category && (
                <><Link to={`/${catSlug}`}>{product.category.name}</Link><FiChevronRight size={13} /></>
              )}
              {product.subcategory && (
                <><Link to={`/category/${catSlug}/${subSlug}`}>{product.subcategory.name}</Link><FiChevronRight size={13} /></>
              )}
              <span>{product.name}</span>
            </nav>
          </div>
        </div>

        <div className="container">
          <div className="catalog-detail-grid">
            {/* ── Gallery ── */}
            <div className="catalog-gallery">
              <div className="catalog-main-img">
  {activeImg > 0 && (
    <button
      className="gallery-arrow gallery-arrow--left"
      onClick={() => setActiveImg(i => i - 1)}
      aria-label="Previous image"
    >
      <FiChevronLeft size={20} />
    </button>
  )}
  <img
    src={images[activeImg] || FALLBACK}
    alt={product.name}
    onError={e => { e.target.src = FALLBACK; }}
  />
  {activeImg < images.length - 1 && (
    <button
      className="gallery-arrow gallery-arrow--right"
      onClick={() => setActiveImg(i => i + 1)}
      aria-label="Next image"
    >
      <FiChevronRight size={20} />
    </button>
  )}
</div>
              {images.length > 1 && (
                <div className="catalog-thumbnails">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      className={`catalog-thumb ${i === activeImg ? 'active' : ''}`}
                      onClick={() => setActiveImg(i)}
                      aria-label={`Image ${i + 1}`}
                    >
                      <img src={img} alt="" onError={e => { e.target.src = FALLBACK; }} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ── Info panel ── */}
            <div className="catalog-info">
              {product.category && (
                <span className="catalog-category-label">{product.category.name}</span>
              )}
              <h1>{product.name}</h1>
              <div className="catalog-sku">SKU: {product.sku}</div>

              {/* Starting price */}
              {/* {product.startingPrice != null ? (
                <div className="catalog-starting-price">
                  Starting from ₹{parseFloat(product.startingPrice).toLocaleString('en-IN')}
                </div>
              ) : (
                <div className="catalog-por">Price on Request</div>
              )} */}

              {product.shortDescription && (
                <p className="catalog-short-desc">{product.shortDescription}</p>
              )}

              {/* Options */}
              {groups.map(group => (
                <div key={group.id} className="catalog-option">
                  <div className="catalog-option-label">
                    {group.name}: <strong>{selectedOptions[group.id] || '—'}</strong>
                  </div>
                  <div className="option-buttons">
                    {group.values.map(val => {
                      // Check if this value, combined with all other currently selected
                      // options, leads to at least one active variant.
                      // We test a hypothetical selection: { ...selectedOptions, [group.id]: val }
                      const hypothetical = { ...selectedOptions, [group.id]: val };
                      const available = (product.variants || []).some(v => {
                        if (v.status !== 'active') return false;
                        const vOpts = v.selectedOptions || [];
                        // Every group that has a hypothetical selection must match this variant
                        return Object.entries(hypothetical).every(([gId, gVal]) =>
                          vOpts.some(o => String(o.optionGroupId) === String(gId) && o.optionValue === gVal)
                        );
                      });
                      return (
                        <button
                          key={val}
                          className={`option-btn ${selectedOptions[group.id] === val ? 'active' : ''}`}
                          onClick={() => handleOptionSelect(group.id, val)}
                          disabled={!available}
                          aria-pressed={selectedOptions[group.id] === val}
                        >
                          {val}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Variant status */}
              {hasVariants && activeVariant && (
                <div className="catalog-variant-info">
                  <strong>Selected:</strong> {activeVariant.name} &nbsp;·&nbsp;
                  SKU: {activeVariant.sku}
                </div>
              )}
              {hasVariants && !activeVariant && groups.length > 0 && (
                <div className="catalog-no-variant">
                  ⚠ This combination is not available. Please choose different options.
                </div>
              )}

              {/* Quantity */}
              {activeVariant && (
                <div className="catalog-option">
                  <div className="catalog-option-label">Quantity</div>
                  <div className="catalog-qty-row">
                    <div className="qty-control" role="group" aria-label="Quantity">
                      <button
                        onClick={() => handleQtyChange(-1)}
                        disabled={quantity <= (activeVariant.minimumQuantity || 1)}
                        aria-label="Decrease quantity"
                      >
                        <FiMinus size={15} />
                      </button>
                      <input
                        type="number"
                        value={quantity}
                        min={activeVariant.minimumQuantity || 1}
                        step={activeVariant.quantityIncrement || 1}
                        onChange={e => handleQtyInput(e.target.value)}
                        onBlur={e => handleQtyInput(e.target.value)}
                        aria-label="Quantity"
                      />
                      <button onClick={() => handleQtyChange(1)} aria-label="Increase quantity">
                        <FiPlus size={15} />
                      </button>
                    </div>
                  </div>
                  <div className="qty-hint">
                    Min: {activeVariant.minimumQuantity} · Increment: {activeVariant.quantityIncrement}
                  </div>
                </div>
              )}

              {/* Live price */}
              {unitPrice != null && (
                <div className="catalog-live-price">
                  <span className="live-price-amount">₹{totalPrice.toLocaleString('en-IN')}</span>
<span className="live-price-total">₹{unitPrice.toFixed(2)}/pc</span>
                </div>
              )}

              {/* Pricing tiers table */}
              {activeVariant?.pricing?.length > 0 && (
                <div className="pricing-table-section">
                  <h4>Quantity Pricing</h4>
                  <table className="pricing-table">
                    <thead>
                      <tr>
                        <th>Quantity</th>
<th>Total Price</th>
<th>Per Piece</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...activeVariant.pricing]
                        .sort((a, b) => a.quantity - b.quantity)
                        .map((tier, i) => {
                          const isActive = unitPrice != null && parseFloat(tier.price) === unitPrice;
                          return (
                            <tr key={i} className={isActive ? 'active-tier' : ''}>
                              <td>{tier.quantity.toLocaleString('en-IN')}</td>
<td>₹{(parseFloat(tier.price) * tier.quantity).toLocaleString('en-IN')}</td>
<td>₹{parseFloat(tier.price).toFixed(2)}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* CTA */}
              <div className="catalog-cta" ref={ctaRef}>
                {activeVariant ? (
                  <button
                    className={`btn btn-primary ${addedToCart ? 'btn-added' : ''}`}
                    onClick={handleAddToCart}
                    disabled={unitPrice == null}
                  >
                    {addedToCart ? <><FiCheck size={16} /> Added!</> : <><FiShoppingCart size={16} /> Add to Cart</>}
                  </button>
                ) : null}
                <button className="btn btn-outline" onClick={handleGetQuote}>
                  <FiMessageSquare size={16} /> Get Quote
                </button>
              </div>

              {/* Trust */}
              <div className="catalog-trust">
                <div className="trust-item"><FiTruck size={15} /> Fast Delivery</div>
                <div className="trust-item"><FiShield size={15} /> Quality Guaranteed</div>
                
              </div>
            </div>
          </div>

          {/* ── Tabs ── */}
          <div className="catalog-tabs" role="tablist">
            {[
              { id: 'description', label: 'Description' },
              ...(specs.length > 0 ? [{ id: 'specs', label: 'Specifications' }] : []),
              ...(related.length > 0 ? [{ id: 'related', label: 'Related Products' }] : []),
            ].map(tab => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`catalog-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="catalog-tab-panel">
            {activeTab === 'description' && (
              <div
                className="catalog-description"
                dangerouslySetInnerHTML={{ __html: product.detailedDescription || `<p>${product.shortDescription || 'No description available.'}</p>` }}
              />
            )}

            {activeTab === 'specs' && (
              <table className="specs-table">
                <tbody>
                  {specs.map((s, i) => (
                    <tr key={i}>
                      <td>{s.label}</td>
                      <td>{s.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {activeTab === 'related' && related.length > 0 && (
              <div className="related-grid">
                {related.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Sticky mobile CTA ── */}
      {showSticky && (
        <div className="sticky-cta" aria-hidden="true">
          <div>
            {unitPrice != null
              ? <div className="sticky-cta-price">₹{totalPrice?.toLocaleString('en-IN')}</div>
              : <div className="sticky-cta-price" style={{ fontSize: '0.95rem' }}>Price on Request</div>}
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{product.name}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {activeVariant && (
              <button className={`btn btn-primary ${addedToCart ? 'btn-added' : ''}`} onClick={handleAddToCart} style={{ fontSize: '0.88rem' }}>
                {addedToCart ? 'Added!' : <><FiShoppingCart size={14} /> Add to Cart</>}
              </button>
            )}
            <button className="btn btn-outline" onClick={handleGetQuote} style={{ fontSize: '0.88rem' }}>
              Get Quote
            </button>
          </div>
        </div>
      )}

    </>
  );
}